import {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {
  InputTextFormik,
  SelectFormik,
  ValidationErrorModal,
} from '../../../../../../_gori/partials/widgets'
import A1_CONSTANTS from '../../../core/_const'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import UseYupValidate from '../../../../../../_gori/hooks/UseYupValidate'
import A1BatchService from '../../../core/_requests'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import {useAuth} from '../../../../auth'
import {BatchScanResult, OptionKey} from './BatchScanResult'
import {ModalTemplate} from '../../template'

type BatchNameProps = {
  show: boolean
  handleClose: () => void
  type: OptionKey
  handleSuccess: () => void
}

const BATCH_NAME_KEY = 'name'

type FormValues = {
  [BATCH_NAME_KEY]?: string | null
}
const BatchName = ({show, handleClose, type, handleSuccess}: BatchNameProps) => {
  const intl = useIntl()
  const {stringYup} = UseYupValidate()
  const {loadingSwitch} = useAuth()
  const {newCancelToken, isCancel} = useCancelToken()

  const [loading, setLoading] = useState<{loadingBatches: boolean; checkingBatchName: boolean}>({
    loadingBatches: false,
    checkingBatchName: false,
  })
  const [optionsBatches, setOptionsBatches] = useState<any>([])
  const [scanning, setScanning] = useState<{
    isShow: boolean
    title: string
    batchId?: number
    existingBatchInfo?: any
  }>({
    isShow: false,
    title: '',
    existingBatchInfo: undefined,
  })

  const [validationErrors, setValidationErrors] = useState<any>()

  const getBatches = useCallback(async () => {
    setLoading((prev) => ({...prev, loadingBatches: true}))
    const config = {
      params: {
        all: true,
        latest: true,
        status: A1_CONSTANTS.A1_BATCH_STATUS.OPEN,
      },
      cancelToken: newCancelToken(),
    }
    try {
      const [{a1_batches}, {a1_batch}] = await Promise.all([
        A1BatchService.getList(config),
        A1BatchService.getA1BatchLatest({cancelToken: newCancelToken()}),
      ])

      if (isAddNew) {
        const nameBatch = (a1_batch?.id ? a1_batch?.id + 100001 : 100001).toString()
        formik.setFieldValue(BATCH_NAME_KEY, nameBatch)
      }
      setOptionsBatches(
        a1_batches.map((item) => ({
          label: item.name,
          value: item.id,
          order_count: item.order_count,
          from: item.from,
        }))
      )
    } catch (error) {
      if (isCancel(error)) return
    } finally {
      setLoading((prev) => ({...prev, loadingBatches: false}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCancel, newCancelToken])

  const isAddNew = useMemo(() => type === 'new', [type])

  const initValidateSchema = useMemo(() => {
    return {
      [BATCH_NAME_KEY]: stringYup(255, isAddNew ? 'A1_BATCH_NAME' : 'SELECT_A_A1_BATCH'),
    }
  }, [isAddNew, stringYup])

  const handleCloseAndReset = () => {
    handleClose()
    formik.resetForm()
  }

  const handleSubmit = async (values: FormValues) => {
    if (!values?.[BATCH_NAME_KEY]) return

    if (isAddNew) {
      try {
        setLoading((prev) => ({...prev, checkingBatchName: true}))
        await A1BatchService.checkBatchNameExist(values.name, {cancelToken: newCancelToken()})
        setScanning({
          isShow: true,
          title: values.name,
        })
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setLoading((prev) => ({...prev, checkingBatchName: false}))
      }
    } else {
      const targetBatch = optionsBatches.find((batch) => batch.value === values?.name)
      if (!targetBatch) return
      setScanning({
        isShow: true,
        title: targetBatch?.label,
        batchId: targetBatch?.value,
        existingBatchInfo: {
          order_count: targetBatch?.order_count,
          from: targetBatch?.from,
        },
      })
    }
  }

  const formik = useFormik<FormValues>({
    initialValues: {
      [BATCH_NAME_KEY]: null,
    },
    enableReinitialize: true,
    validationSchema: Yup.object().shape(initValidateSchema),
    onSubmit: handleSubmit,
  })

  useEffect(() => {
    if (loadingSwitch) return

    getBatches()
  }, [getBatches, loadingSwitch])

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      <BatchScanResult
        show={scanning.isShow}
        handleClose={() => {
          setScanning({isShow: false, title: ''})
          handleCloseAndReset()
        }}
        setShow={(isShow: boolean) => setScanning((prev) => ({...prev, isShow}))}
        type={type}
        handleSuccess={handleSuccess}
        batchInfo={{
          title: scanning.title,
          batchId: scanning.batchId,
          from: scanning.existingBatchInfo?.from,
          order_count: scanning.existingBatchInfo?.order_count,
        }}
      />

      <ModalTemplate
        id='gori_modal_a1_batch_scan_name'
        show={show}
        handleClose={handleCloseAndReset}
        dialogClassName='modal-size-md h-auto'
        contentClassName='modal-padding'
        title={isAddNew ? 'CREATE_NEW_A1_BATCH' : 'ADD_TO_EXISTING_A1_BATCH'}
        bodyClassName='vh-75 overflow-auto'
        titlePrimaryBtn='NEXT'
        disabledPrimaryBtn={loading.loadingBatches}
        disabledSecondaryBtn={loading.loadingBatches}
        loadingPrimaryBtn={loading.checkingBatchName}
        onClickPrimaryBtn={formik.handleSubmit}
      >
        <div className='mt-6'>
          {isAddNew ? (
            <InputTextFormik
              label={intl.formatMessage({id: 'A1_BATCH_NAME'})}
              formik={formik}
              name={BATCH_NAME_KEY}
              required
              labelClassName={'fw-bolder'}
            />
          ) : (
            <SelectFormik
              label={intl.formatMessage({id: 'SELECT_A_A1_BATCH'})}
              placeholder={intl.formatMessage({id: 'A1_BATCH'})}
              formik={formik}
              name={BATCH_NAME_KEY}
              options={optionsBatches}
              disabled={loading.loadingBatches}
              emptyDefault={false}
              labelClassName={'fw-bolder'}
              required
            />
          )}
        </div>
      </ModalTemplate>
    </>
  )
}

export {BatchName}
